import { getVisaSetttings, VISA_SDK_PROD, VISA_SDK_TEST } from './config';
import AbstractSrcInitiator from './AbstractSrcInitiator';
import SrciError, { MastercardError, VisaError } from './SrciError';
import type {
    CustomSdkConfiguration,
    ISchemeSdk,
    SrciCompleteIdentityValidationResponse,
    SrcIdentityLookupParams,
    SrciIdentityLookupResponse,
    SrcInitParams
} from './types';
import type { IAnalytics } from '../../../../../core/Analytics/Analytics';

const IdentityTypeMap = {
    email: 'EMAIL',
    telephoneNumber: 'MOBILE_NUMBER'
};

type VisaSdkInitParams = SrcInitParams & ReturnType<typeof getVisaSetttings> & { srciTransactionId: string };

export interface IVisaSchemeSdk extends ISchemeSdk {
    init(params: VisaSdkInitParams): Promise<void>;
    identityLookup(params: { identityValue: string; type: string }): Promise<SrciIdentityLookupResponse>;
    completeIdentityValidation(otp: string): Promise<SrciCompleteIdentityValidationResponse>;
}

class VisaSdk extends AbstractSrcInitiator {
    public readonly schemeName = 'visa';
    declare public schemeSdk: IVisaSchemeSdk;

    constructor(environment: string, customSdkConfig: CustomSdkConfiguration, analytics: IAnalytics) {
        super(environment.toLowerCase().includes('live') ? VISA_SDK_PROD : VISA_SDK_TEST, customSdkConfig, analytics);
    }

    protected isSdkIsAvailableOnWindow(): boolean {
        // @ts-ignore vAdapters is created by the VISA sdk
        if (window.vAdapters?.VisaSRCI) return true;
        return false;
    }

    protected assignSdkReference(): void {
        // @ts-ignore vAdapters is created by the VISA sdk
        this.schemeSdk = new window.vAdapters.VisaSRCI();
    }

    public async init(params: SrcInitParams, srciTransactionId: string): Promise<void> {
        try {
            const sdkProps = {
                ...params,
                ...getVisaSetttings(this.customSdkConfiguration),
                srciTransactionId
            };

            await this.schemeSdk.init(sdkProps);
            const systemIframe = document.getElementById('vcop-src-system-frame');
            systemIframe?.setAttribute('aria-hidden', 'true');
            systemIframe?.setAttribute('tabindex', '-1');
            systemIframe?.setAttribute('inert', '');
        } catch (err) {
            const srciError = new SrciError(err as VisaError | MastercardError, 'init', this.schemeName);
            throw srciError;
        }
    }

    public async identityLookup({ identityValue, type }: SrcIdentityLookupParams): Promise<SrciIdentityLookupResponse> {
        try {
            const consumerIdentity = {
                identityValue,
                type: IdentityTypeMap[type]
            };

            const response = await this.schemeSdk.identityLookup(consumerIdentity);
            return response;
        } catch (err) {
            const srciError = new SrciError(err as VisaError | MastercardError, 'identityLookup', this.schemeName);
            throw srciError;
        }
    }

    public async completeIdentityValidation(otp: string): Promise<SrciCompleteIdentityValidationResponse> {
        try {
            const response = await this.schemeSdk.completeIdentityValidation(otp);
            return response;
        } catch (err: unknown) {
            const srciError = new SrciError(err as VisaError | MastercardError, 'completeIdentityValidation', this.schemeName);
            throw srciError;
        }
    }
}

export default VisaSdk;
